import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

// Mirrors apps/api/src/access/routes.ts. During early access anyone can create a workspace, on
// Free; an invite code or an approved request puts it on Growth, free while we launch.

export interface AccessRequest {
  company: string;
  website: string | null;
  note: string | null;
  requestedAt: number;
  /** The workspace it's for, if asked from one. */
  organizationId: string | null;
  decision: "approved" | "declined" | null;
  decidedAt: number | null;
}

export interface MyAccess {
  /** Paid plans are open (early access is over). */
  open: boolean;
  /** Let in: their workspaces get Growth free during early access. */
  growth: boolean;
  request: AccessRequest | null;
}

export const useMyAccess = (enabled = true) => useQuery({ queryKey: ["my-access"], queryFn: () => api<MyAccess>("/me/access"), enabled });

/** Whether paid plans are open, for signed-out pages. Defaults to early access if it can't tell. */
export const useBillingMode = () =>
  useQuery({
    queryKey: ["billing-mode"],
    queryFn: async () => {
      const res = await fetch("/api/billing-mode").catch(() => null);
      return res?.ok ? ((await res.json()) as { open: boolean }) : { open: false };
    },
    staleTime: Infinity,
  });

export const redeemInvite = (code: string) => api<{ growth: boolean }>("/me/access/code", { method: "POST", json: { code } });
export const requestGrowth = (input: { company: string; website: string | null; note: string | null; orgId: string }) =>
  api<{ growth: boolean; request: AccessRequest | null }>("/me/access/request", { method: "POST", json: input });

// An invite link (/signup?invite=MK-…) is remembered for the session, so the code is waiting on
// the next screen after the account is created.
const INVITE_KEY = "mk-invite";

export function rememberInvite(params: URLSearchParams): void {
  const code = params.get("invite");
  if (!code) return;
  try {
    sessionStorage.setItem(INVITE_KEY, code.slice(0, 40));
  } catch {
    /* storage blocked */
  }
}

export function rememberedInvite(): string {
  try {
    return sessionStorage.getItem(INVITE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function forgetInvite(): void {
  try {
    sessionStorage.removeItem(INVITE_KEY);
  } catch {
    /* storage blocked */
  }
}
